<template>
  <div v-if="visible" class="export-modal-overlay" @click.self="handleClose">
    <div class="export-modal" role="dialog" aria-modal="true" aria-labelledby="coupon-export-modal-title">
      <div class="export-modal__header">
        <h2 id="coupon-export-modal-title">匯出用券名單</h2>
        <button type="button" class="export-modal__close" aria-label="關閉" @click="handleClose">
          <i class="ri-close-line"></i>
        </button>
      </div>

      <div class="export-modal__body">
        <div class="export-option-group">
          <label class="export-option-label">匯出粒度</label>
          <div class="export-option-items">
            <label class="export-radio">
              <input v-model="mode" type="radio" value="detail" />
              <span>
                <strong>逐張券</strong>
                <small>一張券一列，含券碼與用券狀態</small>
              </span>
            </label>
            <label class="export-radio">
              <input v-model="mode" type="radio" value="summary" />
              <span>
                <strong>逐會員彙總</strong>
                <small>一會員一列，含發放張數與已用張數</small>
              </span>
            </label>
          </div>
        </div>

        <div class="export-option-group">
          <label class="export-option-label">檔案格式</label>
          <div class="export-option-items">
            <label class="export-radio">
              <input v-model="format" type="radio" value="csv" />
              <span><strong>CSV</strong></span>
            </label>
            <label class="export-radio">
              <input v-model="format" type="radio" value="excel" />
              <span><strong>Excel</strong></span>
            </label>
          </div>
        </div>
      </div>

      <div class="export-modal__footer">
        <button type="button" class="btn btn-secondary" :disabled="exporting" @click="handleClose">取消</button>
        <button type="button" class="btn btn-primary" :disabled="exporting" @click="handleExport">
          <i class="ri-download-line"></i>
          {{ exporting ? '匯出中...' : '匯出' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { couponService } from '@/api/modules/coupons.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'CouponExportModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /**
     * 匯出篩選條件：templateId / batchIssuanceId（至少擇一）、status（選填）
     */
    params: {
      type: Object,
      required: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const mode = ref('detail');
    const format = ref('csv');
    const exporting = ref(false);

    const handleClose = () => {
      if (exporting.value) return;
      emit('close');
    };

    const handleExport = async () => {
      exporting.value = true;
      try {
        const blob = await couponService.exportIssues({
          ...props.params,
          mode: mode.value,
          format: format.value
        });

        const modeLabel = mode.value === 'summary' ? '會員彙總' : '逐張券';
        const extension = format.value === 'excel' ? 'xlsx' : 'csv';
        const dateStr = new Date().toISOString().substring(0, 10);

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `用券名單_${modeLabel}_${dateStr}.${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showSuccess('成功', '用券名單已匯出');
        emit('close');
      } catch (err) {
        console.error('匯出用券名單失敗:', err);
        showError('匯出失敗', err.response?.data?.message || err.message || '匯出用券名單失敗');
      } finally {
        exporting.value = false;
      }
    };

    return {
      mode,
      format,
      exporting,
      handleClose,
      handleExport
    };
  }
};
</script>

<style scoped>
.export-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
}

.export-modal {
  background: #fff;
  border-radius: 8px;
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.export-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.export-modal__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.export-modal__close {
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.export-modal__close:hover {
  color: #333;
}

.export-modal__body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.export-option-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.export-option-label {
  font-size: 14px;
  font-weight: 600;
  color: #555;
  margin: 0;
}

.export-option-items {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.export-radio {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
}

.export-radio:hover {
  border-color: #4a90d9;
  background: #f6faff;
}

.export-radio:has(input:checked) {
  border-color: #4a90d9;
  background: #f0f7ff;
}

.export-radio input {
  margin-top: 3px;
  flex-shrink: 0;
}

.export-radio span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.export-radio strong {
  font-size: 14px;
  color: #333;
}

.export-radio small {
  font-size: 12px;
  color: #888;
}

.export-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}
</style>
